/**
 * VERIFIED CERTIFICATIONS DATA
 *
 * POLICY: This is the sole source-of-truth for rendering certifications.
 * - Only certifications with official documentation are included
 * - Each certification has been verified against the provided PDF list
 * - No unverified or unearned certifications are displayed
 * - All items must include proper institution, completion date, and duration
 */
export interface VerifiedCertification {
  id: string
  title: string // English title (with Finnish in parentheses)
  institution: string // Issuing institution (typically Duodecim)
  completedDate: string // ISO date format (YYYY-MM-DD)
  displayDate: string // Human-readable date (e.g., "Nov 2024")
  year: number // Year of completion
  durationMin: number // Duration in minutes
  duration: string // Human-readable duration (e.g., "55 min")
  focus: string // Focus area/domain
  description: string // Description of certification content
  relevanceSG: string // Relevance to Singapore healthcare
  verifyUrl: string // URL to verify at issuing institution
  featured?: boolean // Optional featured flag
}
// Validation function to ensure certification data integrity
export function isValidCertification(cert: any): cert is VerifiedCertification {
  return (
    cert &&
    typeof cert.id === 'string' &&
    typeof cert.title === 'string' &&
    cert.title.length > 0 &&
    typeof cert.institution === 'string' &&
    /Duodecim|Finnish Nursing Association|American Heart Association/i.test(
      cert.institution,
    ) &&
    typeof cert.completedDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(cert.completedDate) &&
    typeof cert.durationMin === 'number' &&
    typeof cert.focus === 'string' &&
    typeof cert.relevanceSG === 'string'
  )
}
// The verified certifications list - matches exactly with provided PDF documentation
export const verifiedCertifications: VerifiedCertification[] = [
  {
    id: 'youth-psychiatric-care-2024',
    title:
      'Youth Psychiatric Care: Anxious and Sleepless Youth (Nuorisopsykiatrinen näkökulma)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-11-25',
    displayDate: 'Nov 2024',
    year: 2024,
    durationMin: 55,
    duration: '55 min',
    focus: 'Mental Health',
    description:
      'Lecture on psychiatric care approaches for youth experiencing anxiety and sleep disorders, with assessment and intervention strategies.',
    relevanceSG:
      "Growing adolescent mental health needs in Singapore's high-pressure academic environment",
    verifyUrl: 'https://www.duodecim.fi/english/',
    featured: true,
  },
  {
    id: 'geriatric-frailty-2024',
    title: 'Geriatric Frailty Recognition (Gerastenian tunnistaminen)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-10-06',
    displayDate: 'Oct 2024',
    year: 2024,
    durationMin: 30,
    duration: '30 min',
    focus: 'Elderly Care',
    description:
      'Online course on identifying frailty in elderly patients, risk stratification, and appropriate care pathways.',
    relevanceSG:
      "Singapore's aging population (fastest in Asia) requires specialized geriatric assessment skills",
    verifyUrl: 'https://www.duodecim.fi/english/',
    featured: true,
  },
  {
    id: 'malnutrition-risk-2024',
    title:
      'Malnutrition Risk Across Patient Groups (Vajaaravitsemusriski eri potilasryhmissä)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-10-06',
    displayDate: 'Oct 2024',
    year: 2024,
    durationMin: 30,
    duration: '30 min',
    focus: 'Clinical Nutrition',
    description:
      'Assessment and management of malnutrition risks in diverse patient populations, including screening protocols.',
    relevanceSG:
      "Critical for post-surgical recovery and elderly care in Singapore's healthcare facilities",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'safe-discharge-2024',
    title: 'Safe Hospital Discharge Planning (Turvallinen kotiuttaminen)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-10-06',
    displayDate: 'Oct 2024',
    year: 2024,
    durationMin: 30,
    duration: '30 min',
    focus: 'Care Coordination',
    description:
      'Comprehensive discharge planning protocols to ensure safe transitions from hospital to home or care facilities.',
    relevanceSG:
      "Reduces readmission rates, a key KPI for Singapore's private and public hospitals",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'geriatric-pharmacotherapy-2024',
    title: 'Geriatric Pharmacotherapy (Vanhusten lääkehoito)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-09-23',
    displayDate: 'Sep 2024',
    year: 2024,
    durationMin: 60,
    duration: '60 min',
    focus: 'Medication Management',
    description:
      'Evidence-based approaches to medication management in elderly patients, considering physiological changes and comorbidities.',
    relevanceSG:
      "Polypharmacy management for Singapore's aging population with multiple chronic conditions",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'elderly-polypharmacy-2024',
    title: 'Elderly Polypharmacy Management (Iäkkään monilääkitys)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-09-23',
    displayDate: 'Sep 2024',
    year: 2024,
    durationMin: 40,
    duration: '40 min',
    focus: 'Medication Safety',
    description:
      'Strategies for managing multiple medications in elderly patients to minimize adverse effects and optimize therapeutic outcomes.',
    relevanceSG:
      "Prevents adverse drug interactions in Singapore's elderly population with multiple prescriptions",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'fall-prevention-2024',
    title: 'Fall Prevention Strategies (Kaatumisten ehkäisy)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-09-23',
    displayDate: 'Sep 2024',
    year: 2024,
    durationMin: 60,
    duration: '60 min',
    focus: 'Patient Safety',
    description:
      'Evidence-based approaches to prevent falls in healthcare settings, including risk assessment and environmental modifications.',
    relevanceSG:
      "Top priority in Singapore's geriatric care facilities and post-operative rehabilitation",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'telephone-assessment-2024',
    title:
      'Telephone-based Care Needs Assessment (Puhelimessa tapahtuva hoidon tarpeen arviointi)',
    institution: 'Duodecim Medical Society',
    completedDate: '2024-03-20',
    displayDate: 'Mar 2024',
    year: 2024,
    durationMin: 40,
    duration: '40 min',
    focus: 'Telehealth',
    description:
      'Protocols for assessing patient needs via telephone, including triage decision-making and documentation.',
    relevanceSG:
      "Remote patient triage becoming increasingly important in Singapore's digital health initiatives",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'neurological-assessment-2023',
    title:
      'Neurological Patient Emergency Assessment (Neurologisen potilaan päivystysarvio)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-11-08',
    displayDate: 'Nov 2023',
    year: 2023,
    durationMin: 60,
    duration: '60 min',
    focus: 'Emergency Care',
    description:
      'Systematic approach to neurological assessment in emergency situations, including stroke protocols and rapid intervention.',
    relevanceSG:
      "Critical for A&E and ICU settings in Singapore's acute care hospitals",
    verifyUrl: 'https://www.duodecim.fi/english/',
    featured: true,
  },
  {
    id: 'central-venous-catheter-2023',
    title:
      'Central Venous Catheter Insertion & Use (Keskuslaskimokatetrin laitto ja käyttö)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-11-07',
    displayDate: 'Nov 2023',
    year: 2023,
    durationMin: 70,
    duration: '70 min',
    focus: 'Advanced Clinical Skills',
    description:
      'Advanced techniques for central venous catheter insertion, maintenance, and complication management.',
    relevanceSG:
      "High-acuity patient care capability essential for Singapore's tertiary hospitals",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'ecg-ischemia-2023',
    title: 'ECG & Ischemia Recognition (EKG ja iskemia)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-11-07',
    displayDate: 'Nov 2023',
    year: 2023,
    durationMin: 30,
    duration: '30 min',
    focus: 'Cardiac Care',
    description:
      'Identification of ischemic patterns on ECG and appropriate interventions for cardiac emergencies.',
    relevanceSG:
      'Essential for cardiac units and A&E departments in Singapore hospitals',
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'ecg-interpretation-2023',
    title:
      'ECG Fundamentals & Systematic Interpretation (EKG:n perusteet ja systemaattinen tulkinta)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-11-07',
    displayDate: 'Nov 2023',
    year: 2023,
    durationMin: 45,
    duration: '45 min',
    focus: 'Diagnostic Skills',
    description:
      'Systematic approach to ECG interpretation, including rhythm analysis and recognition of common abnormalities.',
    relevanceSG:
      "Supports autonomous clinical decision-making in Singapore's healthcare settings",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'abcde-assessment-2023',
    title:
      'Systematic Assessment of Vital Functions (ABCDE) (Peruselintoimintojen systemaattinen arviointi ABCDE-periaatteella)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-10-31',
    displayDate: 'Oct 2023',
    year: 2023,
    durationMin: 30,
    duration: '30 min',
    focus: 'Critical Care',
    description:
      'Structured approach to patient assessment using the ABCDE (Airway, Breathing, Circulation, Disability, Exposure) methodology.',
    relevanceSG:
      "Gold standard for emergency response in Singapore's healthcare system",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'healthcare-data-protection-2023',
    title: 'Healthcare Data Protection (Tietosuoja terveydenhuollossa)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-09-26',
    displayDate: 'Sep 2023',
    year: 2023,
    durationMin: 30,
    duration: '30 min',
    focus: 'Compliance',
    description:
      'Guidelines for protecting patient data in healthcare settings, including legal requirements and best practices.',
    relevanceSG:
      "PDPA compliance essential in Singapore's stringent data protection environment",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'care-plan-decisions-2023',
    title:
      'Care Plan & Care Line Decisions (Hoitosuunnitelma ja hoidon linjaukset)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-09-26',
    displayDate: 'Sep 2023',
    year: 2023,
    durationMin: 40,
    duration: '40 min',
    focus: 'Care Coordination',
    description:
      'Development of comprehensive care plans and decision-making frameworks for patient management.',
    relevanceSG:
      "Systematic approach to patient management aligns with Singapore's integrated care initiatives",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'pressure-ulcer-prevention-2023',
    title: 'Prevent Pressure Ulcer (Estä painehaava)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-01-23',
    displayDate: 'Jan 2023',
    year: 2023,
    durationMin: 60,
    duration: '60 min',
    focus: 'Wound Care',
    description:
      'Evidence-based strategies for preventing pressure ulcers, including risk assessment and intervention protocols.',
    relevanceSG:
      "Key quality indicator for patient safety in Singapore's healthcare facilities",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'infection-prevention-2023',
    title:
      'Standard Precautions for Infection Prevention (Tavanomaiset varotoimet infektioiden torjunnassa)',
    institution: 'Duodecim Medical Society',
    completedDate: '2023-01-17',
    displayDate: 'Jan 2023',
    year: 2023,
    durationMin: 45,
    duration: '45 min',
    focus: 'Infection Prevention',
    description:
      'Standard precautions for preventing healthcare-associated infections, including hand hygiene and personal protective equipment.',
    relevanceSG:
      "Post-COVID heightened importance in Singapore's infection control protocols",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'guidance-learning-2022',
    title: 'Basics of Guidance and Learning (Ohjauksen ja oppimisen perusteet)',
    institution: 'Duodecim Medical Society',
    completedDate: '2022-12-11',
    displayDate: 'Dec 2022',
    year: 2022,
    durationMin: 30,
    duration: '30 min',
    focus: 'Health Coaching',
    description:
      'Fundamental principles of patient education and learning, including teaching methodologies and assessment.',
    relevanceSG:
      "Patient empowerment focus aligns with Singapore's healthcare education initiatives",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'motivational-interviewing-2022',
    title: 'Motivational Interviewing (Motivoiva keskustelu)',
    institution: 'Duodecim Medical Society',
    completedDate: '2022-12-11',
    displayDate: 'Dec 2022',
    year: 2022,
    durationMin: 60,
    duration: '60 min',
    focus: 'Behavioral Change',
    description:
      'Techniques for motivational interviewing to facilitate behavior change in patients with chronic conditions.',
    relevanceSG:
      "Valuable for Singapore's chronic disease management programs and lifestyle interventions",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
  {
    id: 'patient-safety-intro-2022',
    title: 'Introduction to Patient Safety (Johdatus potilasturvallisuuteen)',
    institution: 'Duodecim Medical Society',
    completedDate: '2022-12-11',
    displayDate: 'Dec 2022',
    year: 2022,
    durationMin: 15,
    duration: '15 min',
    focus: 'Quality Improvement',
    description:
      'Foundation concepts in patient safety, including error prevention and safety culture development.',
    relevanceSG:
      "Foundational for safety culture in Singapore's healthcare organizations",
    verifyUrl: 'https://www.duodecim.fi/english/',
  },
]
// Focus/domain options for filtering - derived from actual certifications
export const focusOptions = [
  'All Areas',
  'Mental Health',
  'Elderly Care',
  'Clinical Nutrition',
  'Care Coordination',
  'Medication Management',
  'Medication Safety',
  'Patient Safety',
  'Telehealth',
  'Emergency Care',
  'Advanced Clinical Skills',
  'Cardiac Care',
  'Diagnostic Skills',
  'Critical Care',
  'Compliance',
  'Wound Care',
  'Infection Prevention',
  'Health Coaching',
  'Behavioral Change',
  'Quality Improvement',
]
// Year options for filtering
export const yearOptions = ['All Years', '2024', '2023', '2022']
// Institution options for filtering
export const institutionOptions = [
  'All Institutions',
  'Duodecim Medical Society',
  'Finnish Nursing Association',
]
// Certification verification policy
export const certificationPolicy = `
CERTIFICATION VERIFICATION POLICY:
This page displays only certifications that have been officially earned and verified.
Each certification has been validated against official documentation from the issuing institution.
No unverified or unearned certifications are included in this showcase.
All certifications include direct verification links to the issuing organization.
For verification inquiries, please contact the issuing institution directly through the "Verify at Source" links.
`
